/**
 * `useSiloMutation` / `useInstanceMutation` — the two write wrappers every
 * `client/data` mutation hook builds on.
 *
 * These are the write-side counterparts to `useSiloQuery` / `useInstanceQuery`,
 * and exist for the same reason: the silo, the space, and the locale are all
 * ambient in the client, so a hook that threads them by hand repeats the same
 * lines and can forget one. A write always names the locale its text is
 * authored in, so the injected locale is the datastore's language and never
 * `'*'`.
 *
 * Inject the space only when the write always targets the space being viewed.
 * A hook whose caller picks the space — a dashboard row, a space it just
 * created — takes `useSiloMutation` and keeps `spaceKey` as a caller field.
 */
import type { ProcedureUtils } from '@orpc/react-query';
import type { IsoLocale } from '@repo/shared/locale';
import { type UseMutationOptions, type UseMutationResult, useMutation } from '@tanstack/react-query';
import { useSiloLanguage } from 'component/translation';
import { useInstanceKey, useSiloKey } from 'util/datastore';

type SiloInput = { siloKey: string; locale?: IsoLocale };
type InstanceInput = SiloInput & { spaceKey: string };

/**
 * Everything `useMutation` accepts except `mutationFn`, which the helper owns,
 * plus:
 * - `procedure`: the `ProcedureUtils` to call, e.g. `utils.channels.create`.
 *   `TInput` and `TOutput` are inferred from it, so a wrong-shaped write is a
 *   compile error.
 * - `input`: maps the variables a caller passes to the procedure's input, for a
 *   hook that deliberately exposes a friendlier shape than the API's. Omit it
 *   and the variables *are* the input, minus the injected fields. Type its
 *   parameter `void` — `input: (_: void) => ({…})` — for a write whose input is
 *   fixed by the hook's own arguments, so callers write `save()` rather than
 *   `save({})`.
 */
type WriteOptions<TInput, TOutput, TVars, TContext> = Omit<UseMutationOptions<TOutput, Error, TVars, TContext>, 'mutationFn'> & {
    procedure: ProcedureUtils<any, TInput, TOutput, any>;
    input?: (vars: TVars) => Partial<TInput>;
};

export function useSiloMutation<TInput extends SiloInput, TOutput, TVars = Omit<TInput, 'siloKey' | 'locale'>, TContext = unknown>({
    procedure,
    input,
    ...options
}: WriteOptions<TInput, TOutput, TVars, TContext>): UseMutationResult<TOutput, Error, TVars, TContext> {
    const siloKey = useSiloKey();
    const locale = useSiloLanguage();
    const { mutationKey } = procedure.mutationOptions();
    return useMutation({
        mutationKey,
        ...options,
        // The cast is what `Omit` costs: TypeScript can't see that putting the
        // injected keys back onto the caller's variables reconstitutes
        // `TInput`. `useSiloQuery` does the same.
        mutationFn: (vars: TVars) => procedure.call({ ...(input ? input(vars) : vars), siloKey, locale } as TInput),
    });
}

/** `useSiloMutation` for a write that always targets the space being viewed. */
export function useInstanceMutation<
    TInput extends InstanceInput,
    TOutput,
    TVars = Omit<TInput, 'siloKey' | 'spaceKey' | 'locale'>,
    TContext = unknown,
>({ procedure, input, ...options }: WriteOptions<TInput, TOutput, TVars, TContext>): UseMutationResult<TOutput, Error, TVars, TContext> {
    const siloKey = useSiloKey();
    const spaceKey = useInstanceKey();
    const locale = useSiloLanguage();
    const { mutationKey } = procedure.mutationOptions();
    return useMutation({
        mutationKey,
        ...options,
        mutationFn: (vars: TVars) => procedure.call({ ...(input ? input(vars) : vars), siloKey, spaceKey, locale } as TInput),
    });
}
